import { type ComponentChildren, createContext } from "preact";
import { createPortal } from "preact/compat";
import { useCallback, useContext, useEffect, useMemo, useState } from "preact/hooks";
import { Toast, type ToastVariant } from "../components/Toast";

type ToastItem = {
  id: string;
  message: string;
  variant: ToastVariant;
  duration?: number;
};

type ToastContextType = Record<ToastVariant, (message: string, duration?: number) => void>;

const ToastContext = createContext<ToastContextType | undefined>(undefined);

const SILENT: ToastContextType = { success: () => {}, error: () => {}, info: () => {}, warning: () => {} };

export const ToastProvider = ({ children }: { children: ComponentChildren }) => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const add = useCallback((variant: ToastVariant) => {
    return (message: string, duration?: number) => {
      const id = `toast-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
      setToasts((previous) => [...previous, { id, message, variant, duration }]);
    };
  }, []);

  const remove = useCallback((id: string) => setToasts((previous) => previous.filter((toast) => toast.id !== id)), []);

  const toast = useMemo<ToastContextType>(() => ({ success: add("success"), error: add("error"), info: add("info"), warning: add("warning") }), [add]);

  return (
    <ToastContext.Provider value={toast}>
      {children}
      {isMounted &&
        createPortal(
          <div class="fixed top-4 right-4 left-4 z-[9999] flex flex-col space-y-2 pointer-events-none">
            <div class="space-y-2 pointer-events-auto">
              {toasts.map((item) => (
                <Toast key={item.id} id={item.id} message={item.message} variant={item.variant} duration={item.duration} onClose={remove} />
              ))}
            </div>
          </div>,
          document.body,
        )}
    </ToastContext.Provider>
  );
};

/** `toast.success("Saved")`; outside a provider (the prerender) the calls do nothing. */
export const useToast = (): ToastContextType => useContext(ToastContext) ?? SILENT;
